import { Row } from '@tanstack/react-table';
import { TableScrollHandle } from '@experiences/components/table/types';
import { useCallback, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

interface Anchor {
  fallbackIndex: number;
  rowKey: string;
}

// Persist anchors in runtime memory across screens
const anchorCache = new Map<string, Anchor>();

interface UseTableScrollAnchorArgs<TData> {
  getRowKey: (row: Row<TData>) => string;
  isReady: boolean;
  scope: string;
  scrollHandleRef: React.RefObject<TableScrollHandle | null>;
}

export const useTableScrollAnchor = <TData>({
  getRowKey,
  isReady,
  scope,
  scrollHandleRef,
}: UseTableScrollAnchorArgs<TData>) => {
  const rowsRef = useRef<Row<TData>[]>([]);
  const [initialScrollIndex] = useState<number | undefined>(
    () => anchorCache.get(scope)?.fallbackIndex,
  );

  const recordAnchorForRow = useCallback(
    (row: Row<TData>, index: number) => {
      anchorCache.set(scope, {
        fallbackIndex: index,
        rowKey: getRowKey(row),
      });
    },
    [getRowKey, scope],
  );

  const recordCurrentTopmostRow = useCallback(() => {
    const topIndex = scrollHandleRef.current?.getTopmostVisibleIndex();
    if (topIndex === null || topIndex === undefined) {
      return;
    }

    const row = rowsRef.current[topIndex];
    if (!row) {
      return;
    }

    anchorCache.set(scope, {
      fallbackIndex: topIndex,
      rowKey: getRowKey(row),
    });
  }, [getRowKey, scope, scrollHandleRef]);

  const restoreScrollPosition = useCallback(
    (rows = rowsRef.current) => {
      const anchor = anchorCache.get(scope);
      const scrollHandle = scrollHandleRef.current;
      if (!anchor || rows.length === 0 || !scrollHandle) {
        return;
      }

      const matchIndex = rows.findIndex(
        (row) => getRowKey(row) === anchor.rowKey,
      );
      const targetIndex =
        matchIndex >= 0
          ? matchIndex
          : Math.min(anchor.fallbackIndex, rows.length - 1);

      scrollHandle.scrollToIndex(targetIndex);
      anchorCache.delete(scope);
    },
    [getRowKey, scope, scrollHandleRef],
  );

  useFocusEffect(
    useCallback(() => {
      if (!isReady) {
        return;
      }

      const frame = requestAnimationFrame(() => {
        restoreScrollPosition();
      });

      return () => cancelAnimationFrame(frame);
    }, [isReady, restoreScrollPosition]),
  );

  const onRowsChange = useCallback(
    (rows: Row<TData>[]) => {
      rowsRef.current = rows;
      if (isReady) {
        restoreScrollPosition(rows);
      }
    },
    [isReady, restoreScrollPosition],
  );

  return {
    initialScrollIndex,
    onRowsChange,
    recordAnchorForRow,
    recordCurrentTopmostRow,
  };
};
